import './rater.schedule-rating.scss';

import {
  Box,
  FormLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  IUnderwritingScheduleRating,
  ModifierNoteLabel,
  NoteTargetAreaType,
} from '@converge/graphql';
import { NumberField, RaterNotesButton, RaterNotesRow } from '../common';
import { ReactNode, useMemo } from 'react';

import { ConvergeSectionTitle } from '../../../../presentation';
import { RaterScheduleRatingDetails } from './rater.schedule-rating-details';
import { config } from '@converge/config';
import { useRaterStateContextSelector } from '../data';
import {
  SR_MAX_ITEM_PERCENTAGE,
  SR_MAX_TOTAL_PERCENTAGE,
  SR_MIN_ITEM_PERCENTAGE,
  SR_MIN_TOTAL_PERCENTAGE,
} from '../data/rater.constants';

interface ITableCellProps extends TableCellProps {
  children?: ReactNode;
}

interface IRaterScheduleRatingProps {
  showTitle?: boolean;
  scheduleRatings: IUnderwritingScheduleRating[];
  modifier?: number | null;
  hideModifier?: boolean;
  onChange: (uuid: string, percentage: number) => void;
  minPercentage?: number;
  maxPercentage?: number;
  minTotalPercentage?: number;
  maxTotalPercentage?: number;
}

export const RaterScheduleRating = ({
  showTitle = true,
  scheduleRatings,
  modifier,
  hideModifier = false,
  onChange,
  minPercentage = SR_MIN_ITEM_PERCENTAGE,
  maxPercentage = SR_MAX_ITEM_PERCENTAGE,
  minTotalPercentage = SR_MIN_TOTAL_PERCENTAGE,
  maxTotalPercentage = SR_MAX_TOTAL_PERCENTAGE,
}: IRaterScheduleRatingProps) => {
  const {
    isEditable,
    isRequestingRatingUpdate,
    upsertQuoteIsLoading,
    uuid,
    inForcePolicy,
  } = useRaterStateContextSelector(
    ({
      isEditable,
      isRequestingRatingUpdate,
      upsertQuoteIsLoading,
      quote: { uuid, inForcePolicy },
    }) => {
      return {
        isEditable,
        isRequestingRatingUpdate,
        upsertQuoteIsLoading,
        uuid,
        inForcePolicy,
      };
    }
  );

  const totalScheduleRating = useMemo(() => {
    return (
      scheduleRatings.reduce(
        (acc, curr) => acc + (curr.quote.percentage ?? 0),
        0
      ) * 100
    );
  }, [scheduleRatings]);

  const renderRatingTable = () => (
    <Table className="schedule-rating-table">
      <TableHead>
        <TableRow>
          <RaterScheduleRatingTableHeaderCell width={'35%'} />
          <RaterScheduleRatingTableHeaderCell width={'15%'}>
            Minimum %
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell width={'15%'}>
            Maximum %
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell width={'15%'}>
            Selected %
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell width={'10%'} />
        </TableRow>
      </TableHead>
      <TableBody>
        {scheduleRatings.map((rating) => {
          const {
            name,
            uuid: ratingUuid,
            hasChanged,
            quote: { percentage, uuid: quoteUuid },
          } = rating;
          return (
            <RaterScheduleRatingDetails
              uuid={ratingUuid}
              hasChanged={hasChanged}
              percentage={percentage}
              onChange={onChange}
              minPercentage={minPercentage}
              maxPercentage={maxPercentage}
              name={name}
              upsertQuoteIsLoading={upsertQuoteIsLoading}
              isEditable={isEditable && !inForcePolicy}
              isRequestingRatingUpdate={isRequestingRatingUpdate}
              quoteUuid={quoteUuid}
            />
          );
        })}
        <TableRow>
          <RaterScheduleRatingTableHeaderCell>
            Total Schedule Rating Debit/Credit
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell>
            {minTotalPercentage}%
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell>
            {maxTotalPercentage}%
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell>
            {totalScheduleRating.toFixed(0)}%
          </RaterScheduleRatingTableHeaderCell>
          <RaterScheduleRatingTableHeaderCell />
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <>
      {showTitle && (
        <ConvergeSectionTitle mb={1}>Schedule Rating</ConvergeSectionTitle>
      )}

      {renderRatingTable()}

      {!hideModifier && (
        <Stack className="schedule-rating-modifier-container">
          <FormLabel>Schedule rating modifier</FormLabel>
          <Box className="fusionScore-modifier-container" width={105}>
            <NumberField
              name="scheduleRatingModifier"
              value={modifier?.toFixed(2)}
              asValue
              placeholder={config.defaults.scheduleRatingPercentage.toFixed(2)}
              isDisabled
            />
            <RaterNotesButton
              uuid={`${uuid}`}
              openUuid={`${uuid}${ModifierNoteLabel.ScheduleRating}`}
              targetArea={NoteTargetAreaType.Quote}
              label={[ModifierNoteLabel.ScheduleRating]}
            />
          </Box>
          <Box>
            <RaterNotesRow
              colSpan={0}
              uuid={`${uuid}`}
              openUuid={`${uuid}${ModifierNoteLabel.ScheduleRating}`}
              label={[ModifierNoteLabel.ScheduleRating]}
              targetArea={NoteTargetAreaType.Quote}
              isFullWidth={true}
            />
          </Box>
        </Stack>
      )}
    </>
  );
};

const RaterScheduleRatingTableHeaderCell = ({
  children,
  ...otherProps
}: ITableCellProps) => {
  return (
    <TableCell
      className="schedule-rating-cell"
      sx={{ fontWeight: 'bold' }}
      {...otherProps}
    >
      {children}
    </TableCell>
  );
};
